import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaCalendar, FaUser, FaHashtag, FaChevronDown, FaChevronUp, FaEdit, FaEye } from 'react-icons/fa';
import CustomerHeader from '../components/CustomerHeader';
import ticketService from '../services/ticketService';

interface ComplaintRecord {
  uid: string;
  formName: string;
  status: string;
  createdTime: string;
  assignedRole?: string;
  form_number?: string;
  form_uid?: string;
  assigned_to?: string;
  customer_name?: string;
  customer_number?: string;
}

interface TabConfig {
  formName: string;
  tabOrder: number;
  tabName: string;
  tabStatus: string;
}

const ComplaintsPage: React.FC = () => {
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState<ComplaintRecord[]>([]);
  const [allTickets, setAllTickets] = useState<ComplaintRecord[]>([]);
  const [tabs, setTabs] = useState<TabConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('');
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  const formName = 'Raise Concern'; // This is the form name for complaints

  useEffect(() => {
    loadMyComplaints();
  }, []);

  useEffect(() => {
    if (selectedTab && allTickets.length > 0) {
      filterComplaintsByTab();
    }
  }, [selectedTab, allTickets]);

  const loadMyComplaints = async () => {
    setLoading(true);
    try {
      console.log('🔍 Loading My Complaints API...');
      const response = await ticketService.getMyComplaints();

      console.log('📦 My Complaints API response:', response);

      if (response && response.success) {
        // Extract tabs
        const tabsData = response.tabs || [];
        const sortedTabs = tabsData.sort((a: TabConfig, b: TabConfig) => a.tabOrder - b.tabOrder);
        setTabs(sortedTabs);

        // Extract all tickets
        const ticketsData = response.tickets || [];
        setAllTickets(ticketsData);

        // Set initial tab
        if (sortedTabs.length > 0) {
          console.log('📑 Setting initial tab:', sortedTabs[0].tabStatus);
          setSelectedTab(sortedTabs[0].tabStatus);
        }

        console.log(`✅ Loaded ${sortedTabs.length} tabs and ${ticketsData.length} tickets`);
      } else {
        console.error('❌ Failed to load My Complaints:', response);
        setTabs([]);
        setAllTickets([]);
      }
    } catch (error: any) {
      console.error('❌ Error loading My Complaints:', error);
      setTabs([]);
      setAllTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const filterComplaintsByTab = () => {
    console.log('🔍 Filtering complaints for tab:', selectedTab);

    // Filter tickets by selected tab status
    const filtered = allTickets.filter((ticket) => ticket.status === selectedTab);

    console.log(`✅ Filtered ${filtered.length} complaints for status: ${selectedTab}`);
    setComplaints(filtered);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleViewDetails = (record: ComplaintRecord) => {
    const uid = record.form_uid || record.uid;
    const status = record.status;
    navigate(`/dynamic-form/${encodeURIComponent(formName)}?mode=VIEW&uid=${uid}&status=${status}`);
  };

  const handleEdit = (record: ComplaintRecord) => {
    const uid = record.form_uid || record.uid;
    const status = record.status;
    navigate(`/dynamic-form/${encodeURIComponent(formName)}?mode=EDIT&uid=${uid}&status=${status}`);
  };

  const getSortedTabs = () => {
    return tabs;
  };

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCardId(expandedCardId === cardId ? null : cardId);
  };

  const handleRaiseComplaint = () => {
    navigate(`/dynamic-form/${encodeURIComponent(formName)}?mode=ADD`);
  };

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} />
      <ContentContainer>
        <BackButton onClick={() => navigate('/dashboard')}>
          ← Back
        </BackButton>
        <PageTitle>My Complaints</PageTitle>

        {/* Tabs */}
        {tabs.length > 0 && (
          <TabsContainer>
            {getSortedTabs().map((tab) => (
              <Tab
                key={tab.tabStatus}
                active={selectedTab === tab.tabStatus}
                onClick={() => setSelectedTab(tab.tabStatus)}
              >
                {tab.tabName || tab.tabStatus}
              </Tab>
            ))}
          </TabsContainer>
        )}

      {/* Loading State */}
      {loading && (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading complaints...</LoadingText>
        </LoadingContainer>
      )}

      {/* Empty State */}
      {!loading && complaints.length === 0 && (
        <EmptyState>
          <EmptyIcon>📋</EmptyIcon>
          <EmptyText>No complaints found</EmptyText>
          <EmptySubtext>Try adjusting your filters or check back later</EmptySubtext>
        </EmptyState>
      )}

      {/* Complaints Cards */}
      {!loading && complaints.length > 0 && (
        <CardsContainer>
          {complaints.map((complaint) => {
            // Get customer data from localStorage as fallback
            const storedCustomerNumber = localStorage.getItem('customerCode') ||
                                         localStorage.getItem('customerNumber') || '-';
            const storedCustomerName = localStorage.getItem('customerName') || '-';
            const isExpanded = expandedCardId === complaint.uid;

            return (
              <ComplaintCard key={complaint.uid} isExpanded={isExpanded}>
                <CompactHeader onClick={() => toggleCardExpansion(complaint.uid)}>
                  <HeaderLeft>
                    <TransactionNumber>
                      {React.createElement(FaHashtag as any, {})}
                      {complaint.form_number || '-'}
                    </TransactionNumber>
                  </HeaderLeft>
                  <HeaderRight>
                    <IconButton onClick={(e) => { e.stopPropagation(); handleEdit(complaint); }}>
                      {React.createElement(FaEdit as any, {})}
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); handleViewDetails(complaint); }}>
                      {React.createElement(FaEye as any, {})}
                    </IconButton>
                    <ExpandIcon>
                      {isExpanded
                        ? React.createElement(FaChevronUp as any, {})
                        : React.createElement(FaChevronDown as any, {})
                      }
                    </ExpandIcon>
                  </HeaderRight>
                </CompactHeader>

                {isExpanded && (
                  <ExpandedContent>
                    <DetailRow>
                      <DetailLabel>
                        {React.createElement(FaUser as any, {})}
                        Customer Name
                      </DetailLabel>
                      <DetailValue>{complaint.customer_name || storedCustomerName}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {React.createElement(FaHashtag as any, {})}
                        Customer Number
                      </DetailLabel>
                      <DetailValue>{complaint.customer_number || storedCustomerNumber}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {React.createElement(FaUser as any, {})}
                        Assigned To
                      </DetailLabel>
                      <DetailValue>
                        {complaint.assigned_to || `${storedCustomerNumber} - ${storedCustomerName}`}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {React.createElement(FaCalendar as any, {})}
                        Created Date
                      </DetailLabel>
                      <DetailValue>{formatDate(complaint.createdTime)}</DetailValue>
                    </DetailRow>
                    {complaint.assignedRole && (
                      <DetailRow>
                        <DetailLabel>Assigned Role</DetailLabel>
                        <DetailValue>{complaint.assignedRole}</DetailValue>
                      </DetailRow>
                    )}
                  </ExpandedContent>
                )}
              </ComplaintCard>
            );
          })}
        </CardsContainer>
      )}
      </ContentContainer>

      {/* Floating Raise Complaint Button */}
      <RaiseComplaintButton onClick={handleRaiseComplaint}>
        {React.createElement(FaPlus as any, {})}
        Complaint
      </RaiseComplaintButton>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
  padding-bottom: 80px;
`;

const PageTitle = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: translateX(-2px);
  }
`;

const RaiseComplaintButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  box-shadow: 0 4px 16px rgba(244, 0, 0, 0.4);
  transition: all 0.3s;
  z-index: 100;

  &:hover {
    background: ${props => props.theme.primaryDark || '#c00000'};
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(244, 0, 0, 0.5);
  }

  &:active {
    transform: translateY(-2px);
  }

  svg {
    font-size: 1.2rem;
  }
`;

const TransactionNumber = styled.div`
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  gap: 0.4rem;

  svg {
    font-size: 0.85rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props.theme.primary};
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const FilterButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TabsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  background: white;
  border-bottom: 2px solid ${props => props.theme.border};
  padding: 0 1rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.primary};
    border-radius: 4px;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.textPrimary};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 3px solid ${props => props.active ? props.theme.primary : 'transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.surface};
  }
`;

const FiltersPanel = styled.div`
  background: white;
  padding: 1rem;
  margin: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FilterRow = styled.div`
  margin-bottom: 1rem;
`;

const FilterLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.textPrimary};
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const FilterActionButton = styled.button<{ secondary?: boolean }>`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 4px;
  background: ${props => props.secondary ? props.theme.surface : props.theme.primary};
  color: ${props => props.secondary ? props.theme.textPrimary : 'white'};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:active {
    transform: scale(0.98);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid ${props => props.theme.border};
  border-top-color: ${props => props.theme.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: ${props => props.theme.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.h3`
  font-size: 1.25rem;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 0.5rem;
`;

const EmptySubtext = styled.p`
  color: ${props => props.theme.textSecondary};
`;

const CardsContainer = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ComplaintCard = styled.div<{ isExpanded: boolean }>`
  background: white;
  border-radius: 8px;
  border: 2px solid ${props => props.isExpanded ? props.theme.primary : '#dee2e6'};
  box-shadow: ${props => props.isExpanded ? '0 4px 12px rgba(0, 0, 0, 0.12)' : '0 2px 4px rgba(0, 0, 0, 0.06)'};
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: 0.75rem;

  &:hover {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const CompactHeader = styled.div`
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: white;
  transition: background 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.primary};
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1rem;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ExpandIcon = styled.div`
  color: ${props => props.theme.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  margin-left: 0.5rem;
  transition: transform 0.3s;
`;

const ExpandedContent = styled.div`
  padding: 0 1.25rem 1.25rem 1.25rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 500px;
    }
  }
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${props => props.theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  svg {
    font-size: 0.75rem;
    opacity: 0.7;
  }
`;

const DetailValue = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.textPrimary};
  font-weight: 500;
  word-break: break-word;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  margin: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PaginationButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  background: ${props => props.theme.primary};
  color: white;
  font-weight: 600;
  cursor: pointer;
  
  &:disabled {
    background: ${props => props.theme.border};
    cursor: not-allowed;
  }
  
  &:not(:disabled):active {
    transform: scale(0.98);
  }
`;

const PageInfo = styled.span`
  font-weight: 500;
  color: ${props => props.theme.textPrimary};
`;

export default ComplaintsPage;
